<template>
  <div class="enhanced-subtitle-editor">
    <!-- 工作流步骤指示器 -->
    <div class="workflow-steps-container">
      <VideoPipelineSteps :current="workflowStep" class="workflow-steps" />
    </div>

    <!-- 字符数统计 -->
    <div class="character-count" v-if="characterCount">
      <a-row :gutter="16">
        <a-col :span="8">
          <a-statistic title="字幕条数" :value="characterCount.subtitle_count" />
        </a-col>
        <a-col :span="8">
          <a-statistic title="原文字符数" :value="characterCount.original_character_count" />
        </a-col>
        <a-col :span="8">
          <a-statistic title="总时长" :value="formatDuration(characterCount.total_duration)" />
        </a-col>
      </a-row>
    </div>

    <!-- 视频播放组件 -->
    <div class="video-player-container" v-if="videoUrl">
      <div class="video-player">
        <video
          ref="videoRef"
          :src="videoUrl"
          controls
          preload="metadata"
          @loadedmetadata="onVideoLoaded"
          @timeupdate="onTimeUpdate"
          @play="onVideoPlay"
          @pause="onVideoPause"
          @seeked="onVideoSeeked"
          class="video-element"
        >
          您的浏览器不支持视频播放
        </video>
      </div>
      <div class="video-controls">
        <a-space>
          <a-button @click="togglePlay" :type="isPlaying ? 'default' : 'primary'" size="small">
            <template #icon>
              <PlayCircleOutlined v-if="!isPlaying" />
              <PauseCircleOutlined v-else />
            </template>
            {{ isPlaying ? '暂停' : '播放' }}
          </a-button>
          <a-button @click="seekBackward" size="small">
            <template #icon><BackwardOutlined /></template>
            后退5秒
          </a-button>
          <a-button @click="seekForward" size="small">
            <template #icon><ForwardOutlined /></template>
            前进5秒
          </a-button>
        </a-space>
        <div class="video-time-info">
          <span class="current-time">{{ formatTime(currentTime) }}</span>
          <span class="separator">/</span>
          <span class="total-time">{{ formatTime(totalDuration) }}</span>
        </div>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="subtitle-toolbar">
      <div class="toolbar-left">
        <a-button @click="addSubtitle" type="primary" size="small">
          <template #icon><PlusOutlined /></template>
          新增字幕
        </a-button>
        <a-button @click="showTimelineView = !showTimelineView" size="small">
          <template #icon><ClockCircleOutlined /></template>
          {{ showTimelineView ? '列表视图' : '时间轴视图' }}
        </a-button>
        <a-button @click="splitSelectedSubtitle" :disabled="selectedSubtitles.length !== 1" size="small">
          <template #icon><SplitCellsOutlined /></template>
          拆分字幕
        </a-button>
        <a-button @click="mergeSelectedSubtitles" :disabled="selectedSubtitles.length < 2" size="small">
          <template #icon><MergeCellsOutlined /></template>
          合并字幕
        </a-button>
        <a-button @click="deleteSelectedSubtitles" :disabled="selectedSubtitles.length === 0" danger size="small">
          <template #icon><DeleteOutlined /></template>
          删除字幕
        </a-button>
        <a-button @click="showBatchOperations = true" size="small">
          <template #icon><SettingOutlined /></template>
          批量操作
        </a-button>
        <a-button @click="saveProject" :loading="saving" size="small">
          <template #icon><SaveOutlined /></template>
          保存
        </a-button>
      </div>
      
      <div class="toolbar-right">
        <a-input-search
          v-model:value="searchText"
          placeholder="搜索字幕内容"
          style="width: 200px"
          size="small"
          @search="onSearch"
        />
        <a-button 
          type="primary" 
          size="small"
          @click="goToTranslationReview"
          :disabled="!canProceedToNext"
        >
          进入翻译校验
        </a-button>
      </div>
    </div>

    <!-- 内容区域包装器 -->
    <div class="subtitle-content-area">
      <!-- 时间轴视图 -->
    <div v-if="showTimelineView" class="timeline-view" ref="timelineRef">
      <div class="timeline-container">
        <div class="timeline-header">
          <div class="time-ruler">
            <div
              v-for="mark in timeMarks"
              :key="mark.time"
              class="time-mark"
              :style="{ left: (mark.time / totalDuration) * 100 + '%' }"
            >
              <div class="time-label">{{ formatTime(mark.time) }}</div>
            </div>
          </div>
        </div>
        
        <div class="timeline-tracks">
          <!-- 播放进度指示器 -->
          <div
            class="playhead"
            :style="{ left: (currentTime / totalDuration) * 100 + '%' }"
          ></div>
          
          <!-- 字幕轨道 -->
          <div class="subtitle-track">
            <div
              v-for="subtitle in sortedSubtitles"
              :key="subtitle.id"
              :class="[
                'subtitle-block',
                {
                  'active': currentSubtitleId === subtitle.id,
                  'highlighted': highlightedSubtitleId === subtitle.id,
                  'selected': selectedSubtitles.includes(subtitle.id),
                  'edited': subtitle.is_edited
                }
              ]"
              :style="{
                left: (subtitle.start_time / totalDuration) * 100 + '%',
                width: ((subtitle.duration || (subtitle.end_time - subtitle.start_time)) / totalDuration) * 100 + '%'
              }"
              @click="onSubtitleClick(subtitle)"
              @contextmenu.prevent="showSubtitleContextMenu($event, subtitle)"
            >
              <div class="subtitle-content">
                <div class="subtitle-text">{{ subtitle.original_text || '空字幕' }}</div>
                <div class="subtitle-time">
                  {{ formatTime(subtitle.start_time) }} - {{ formatTime(subtitle.end_time) }}
                  <span class="duration-text">({{ formatDuration(subtitle.duration || (subtitle.end_time - subtitle.start_time)) }})</span>
                </div>
              </div>
              
              <!-- 调整手柄 -->
              <div
                class="resize-handle resize-start"
                @mousedown="startResize($event, subtitle, 'start')"
              ></div>
              <div
                class="resize-handle resize-end"
                @mousedown="startResize($event, subtitle, 'end')"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 列表视图 -->
    <div v-else class="subtitle-table-container" ref="subtitleListRef">
      <a-table
        :data-source="filteredSubtitles"
        :columns="tableColumns"
        :pagination="false"
        size="small"
        :row-class-name="getRowClassName"
        row-key="id"
      >
        <template #bodyCell="{ column, record, index }">
          <!-- 复选框列 -->
          <template v-if="column.key === 'selection'">
            <a-checkbox
              :checked="isSubtitleSelected(record.id)"
              @change="() => toggleSubtitleSelection(record.id)"
              @click.stop
            />
          </template>
          
          <!-- 序号列 -->
          <template v-if="column.key === 'index'">
            <span 
              class="subtitle-index clickable-index" 
              @click="onIndexClick(record)"
              :title="`点击跳转到 ${formatTime(record.start_time)}`"
            >
              {{ index + 1 }}
            </span>
          </template>
          
          <!-- 开始时间列 -->
          <template v-if="column.key === 'start_time'">
            <a-input
              :value="formatTimeInput(record.start_time)"
              @change="(e: Event) => onTimeInputChange(record, 'start', (e.target as HTMLInputElement).value)"
              size="small"
              style="width: 120px"
              placeholder="HH:MM:SS,ms"
            />
          </template>
          
          <!-- 结束时间列 -->
          <template v-if="column.key === 'end_time'">
            <a-input
              :value="formatTimeInput(record.end_time)"
              @change="(e: Event) => onTimeInputChange(record, 'end', (e.target as HTMLInputElement).value)"
              size="small"
              style="width: 120px"
              placeholder="HH:MM:SS,ms"
            />
          </template>
          
          <!-- Duration列 -->
          <template v-if="column.key === 'duration'">
            <span class="duration-text">
              {{ formatDuration(record.duration || (record.end_time - record.start_time)) }}
            </span>
          </template>
          
          <!-- 字幕内容列 -->
          <template v-if="column.key === 'text'">
            <a-textarea
              :ref="(el: any) => setTextareaRef(record.id, el)"
              v-model:value="record.original_text"
              @change="onTextChange(record, 'original')"
              @focus="onTextareaFocus(record.id)"
              @blur="onTextareaBlur(record.id)"
              @click="(e: any) => onTextareaClick(record.id, e)"
              @keyup="(e: any) => onTextareaKeyup(record.id, e)"
              @select="(e: any) => onTextareaSelect(record.id, e)"
              :auto-size="{ minRows: 1, maxRows: 2 }"
              placeholder="请输入原文字幕"
              :class="{ 'error': !record.original_text }"
              size="small"
            />
          </template>
          
          <!-- 状态列 -->
          <template v-if="column.key === 'status'">
            <div class="subtitle-status">
              <a-tag v-if="record.is_edited" color="orange" size="small">已编辑</a-tag>
              <a-tag v-else color="default" size="small">未编辑</a-tag>
            </div>
          </template>
        </template>
      </a-table>
      
      <!-- 空状态 -->
      <div v-if="filteredSubtitles.length === 0" class="empty-state">
        <a-empty description="暂无字幕数据">
          <template #extra>
            <a-button type="primary" @click="addSubtitle">添加字幕</a-button>
          </template>
        </a-empty>
      </div>
    </div>

    <!-- 批量操作弹窗 -->
    <a-modal
      v-model:open="showBatchOperations"
      title="批量操作"
      width="600px"
      @ok="executeBatchOperation"
      @cancel="resetBatchOperation"
    >
      <a-form :model="batchOperation" layout="vertical">
        <a-form-item label="操作类型">
          <a-radio-group v-model:value="batchOperation.operation">
            <a-radio value="replace">批量替换</a-radio>
            <a-radio value="delete">批量删除</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <template v-if="batchOperation.operation === 'replace'">
          <a-form-item label="搜索文本">
            <a-input v-model:value="batchOperation.search_text" placeholder="请输入要替换的文本" />
          </a-form-item>
          <a-form-item label="替换文本">
            <a-input v-model:value="batchOperation.replace_text" placeholder="请输入替换后的文本" />
          </a-form-item>
        </template>
        
        <template v-if="batchOperation.operation === 'delete'">
          <a-form-item label="要删除的文字">
            <a-textarea
              v-model:value="batchOperation.delete_text"
              placeholder="请输入要删除的文字内容"
              :auto-size="{ minRows: 3, maxRows: 6 }"
            />
          </a-form-item>
        </template>
      </a-form>
    </a-modal>

    <!-- 拆分字幕弹窗 -->
    <a-modal
      v-model:open="showSplitDialog"
      title="拆分字幕"
      width="900px"
      :body-style="{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }"
      @ok="executeSplit"
      @cancel="resetSplitDialog"
    >
      <div class="split-dialog-content">
              
        <div class="split-points-container">
          <div class="split-points-header">
            <span>拆分点列表</span>
            <a-space>
              <a-button type="primary" size="small" @click="addSplitPointSmart">
                <template #icon><PlusOutlined /></template>
                智能添加拆分点
              </a-button>
              <!--
              <a-button size="small" @click="addSplitPoint">
                <template #icon><PlusOutlined /></template>
                手动添加拆分点
              </a-button>
              -->
            </a-space>
          </div>
          
          <div v-if="splitData.split_points.length === 0" class="empty-split-points">
            <a-empty description="暂无拆分点" :image="false">
              <template #extra>
                <a-space>
                  <a-button type="primary" @click="addSplitPointSmart">智能添加拆分点</a-button>
                  <a-button @click="addSplitPoint">手动添加拆分点</a-button>
                </a-space>
              </template>
            </a-empty>
          </div>
          
          <div v-else class="split-points-list">
            <!-- 字幕1文本框 -->
            <div class="subtitle-1-section">
              <div class="subtitle-1-header">
                <span class="subtitle-1-label">字幕 1</span>
                <span class="subtitle-1-time">{{ formatTime(splitData.min_time) }} - {{ formatTime(splitData.split_points[0]?.time || splitData.max_time) }}</span>
              </div>
              <a-form-item label="文本内容" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
                <a-textarea 
                  v-model:value="splitData.first_text" 
                  :auto-size="{ minRows: 2, maxRows: 4 }"
                  placeholder="请输入字幕1的文本内容"
                  @click="(e: any) => onSplitTextareaClick('first_text', e)"
                  @keyup="(e: any) => onSplitTextareaKeyup('first_text', e)"
                  @select="(e: any) => onSplitTextareaSelect('first_text', e)"
                  @focus="onSplitTextareaFocus('first_text')"
                  @blur="onSplitTextareaBlur('first_text')"
                />
              </a-form-item>
            </div>
            
            <!-- 拆分点列表 -->
            <div 
              v-for="(point, index) in getSortedSplitPoints()" 
              :key="point.originalIndex"
              class="split-point-item"
            >
              <div class="split-point-header">
                <span class="split-point-label">拆分点 {{ index + 1 }}</span>
                <a-button 
                  type="text" 
                  danger 
                  size="small" 
                  @click="removeSplitPoint(point.originalIndex)"
                  :disabled="splitData.split_points.length === 1"
                >
                  <template #icon><DeleteOutlined /></template>
                  删除
                </a-button>
              </div>
              
              <div class="split-point-content">
                <a-form-item label="拆分时间" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
                  <a-input
                    :value="formatTimeInput(point.time)"
                    @change="(e: Event) => updateSplitPointTime(point.originalIndex, parseTimeInput((e.target as HTMLInputElement).value))"
                    style="width: 120px"
                    placeholder="HH:MM:SS,ms"
                  />
                </a-form-item>
                
                <a-form-item label="文本内容" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
                  <a-textarea 
                    v-model:value="splitData.split_points[point.originalIndex].text" 
                    :auto-size="{ minRows: 2, maxRows: 4 }"
                    placeholder="请输入该拆分点后的字幕内容"
                    @click="(e: any) => onSplitTextareaClick(`split_point_${point.originalIndex}`, e)"
                    @keyup="(e: any) => onSplitTextareaKeyup(`split_point_${point.originalIndex}`, e)"
                    @select="(e: any) => onSplitTextareaSelect(`split_point_${point.originalIndex}`, e)"
                    @focus="onSplitTextareaFocus(`split_point_${point.originalIndex}`)"
                    @blur="onSplitTextareaBlur(`split_point_${point.originalIndex}`)"
                  />
                </a-form-item>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 拆分预览 -->
        <div v-if="splitData.split_points.length > 0" class="split-preview">
          <a-divider>拆分预览</a-divider>
          <div class="preview-subtitles">
            <div 
              v-for="(preview, index) in getSplitPreview()" 
              :key="index"
              class="preview-subtitle"
            >
              <div class="preview-header">
                <span class="preview-label">字幕 {{ index + 1 }}</span>
                <span class="preview-time">{{ formatTime(preview.start_time) }} - {{ formatTime(preview.end_time) }}</span>
              </div>
              <div class="preview-text">{{ preview.text || '（空内容）' }}</div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>

    <!-- 右键菜单 -->
    <div
      v-if="contextMenu.visible"
      class="context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      @click.stop
    >
      <div class="context-menu-item" @click="onSubtitleAction('split', contextMenu.subtitle!)">
        <SplitCellsOutlined /> 拆分字幕
      </div>
      <div class="context-menu-item" @click="onSubtitleAction('merge', contextMenu.subtitle!)">
        <MergeCellsOutlined /> 合并字幕
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-item" @click="onSubtitleAction('duplicate', contextMenu.subtitle!)">
        <CopyOutlined /> 复制字幕
      </div>
      <div class="context-menu-item danger" @click="onSubtitleAction('delete', contextMenu.subtitle!)">
        <DeleteOutlined /> 删除字幕
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { message } from 'ant-design-vue'
import VideoPipelineSteps from '@/modules/video/components/VideoPipelineSteps.vue'
import { VIDEO_PIPELINE_STEP } from '@/modules/video/constants/video-pipeline-steps'
import {
  PlusOutlined,
  SettingOutlined,
  SaveOutlined,
  MoreOutlined,
  SplitCellsOutlined,
  MergeCellsOutlined,
  DeleteOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  BackwardOutlined,
  ForwardOutlined
} from '@ant-design/icons-vue'

interface Subtitle {
  id: number
  project_id: number
  start_time: number
  end_time: number
  duration?: number
  original_text?: string
  translated_text?: string
  translated_text_multiline?: string
  language: string
  order_index: number
  is_edited: boolean
  modification_type: string
  created_at: string
  updated_at: string
}

interface CharacterCount {
  project_id: number
  language?: string
  original_character_count: number
  subtitle_count: number
  total_duration: number
  last_updated: string
}

interface Props {
  projectId: number
  project?: any
  subtitles: Subtitle[]
  videoUrl?: string
  currentTime?: number
  availableLanguages?: string[]
}

interface Emits {
  (e: 'subtitle-click', subtitle: Subtitle): void
  (e: 'subtitle-update', subtitle: Subtitle): void
  (e: 'subtitle-create', subtitle: Partial<Subtitle>): void
  (e: 'subtitle-delete', subtitleId: number): void
  (e: 'subtitles-merge', subtitleIds: number[]): void
  (e: 'subtitle-split', subtitleId: number, splitData: 
    | { split_time: number; first_text: string; second_text: string }
    | { first_text: string; split_points: Array<{ time: number; text: string }> }
  ): void
  (e: 'batch-operation', operation: any): void
  (e: 'save-project'): void
  (e: 'workflow-next-step'): void
}

const props = withDefaults(defineProps<Props>(), {
  currentTime: 0,
  availableLanguages: () => []
})

const emit = defineEmits<Emits>()

// 响应式数据
const subtitleListRef = ref<HTMLDivElement>()
const timelineRef = ref<HTMLDivElement>()
const videoRef = ref<HTMLVideoElement>()
const searchText = ref('')
const selectedLanguage = ref('')
const selectedSubtitles = ref<number[]>([])
const currentSubtitleId = ref<number | null>(null)
const highlightedSubtitleId = ref<number | null>(null)
const saving = ref(false)
const characterCount = ref<CharacterCount | null>(null)
const showTimelineView = ref(false)

// 视频播放相关
const isPlaying = ref(false)
const currentTime = ref(0)

// 表格相关
const tableColumns = [
  {
    title: '',
    key: 'selection',
    width: 50,
    align: 'center' as const
  },
  {
    title: '#',
    key: 'index',
    width: 60,
    align: 'center' as const
  },
  {
    title: 'Start time',
    key: 'start_time',
    width: 120,
    align: 'center' as const
  },
  {
    title: 'End time',
    key: 'end_time',
    width: 120,
    align: 'center' as const
  },
  {
    title: 'Duration',
    key: 'duration',
    width: 100,
    align: 'center' as const
  },
  {
    title: 'Text',
    key: 'text',
    align: 'left' as const
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    align: 'center' as const
  }
]

// 工作流相关
const workflowStep = ref(VIDEO_PIPELINE_STEP.SUBTITLE_PROCESSING)

// 时间轴相关
const isResizing = ref(false)
const resizeData = ref({
  subtitle: null as Subtitle | null,
  type: 'start' as 'start' | 'end',
  startX: 0,
  startTime: 0
})

// 防抖更新
let updateTimer: NodeJS.Timeout | null = null

// 右键菜单
const contextMenu = ref({
  visible: false,
  x: 0,
  y: 0,
  subtitle: null as Subtitle | null
})

// 批量操作相关
const showBatchOperations = ref(false)
const batchOperation = ref({
  operation: 'replace',
  search_text: '',
  replace_text: '',
  start_time: 0,
  end_time: 0,
  delete_text: ''
})

// 拆分字幕相关
const showSplitDialog = ref(false)
const splitData = ref({
  subtitle_id: 0,
  min_time: 0,
  max_time: 0,
  first_text: '', // 字幕1的文本内容
  split_points: [] as Array<{
    time: number
    text: string
  }>
})

// 光标位置相关
const focusedSubtitleId = ref<number | null>(null)
const cursorPositions = ref<Map<number, number>>(new Map())

// 拆分对话框中的光标位置相关
const splitDialogCursorPositions = ref<Map<string, number>>(new Map())
const focusedSplitTextarea = ref<string | null>(null)

// 计算属性
const filteredSubtitles = computed(() => {
  let filtered = props.subtitles
  


  // 语言过滤
  if (selectedLanguage.value) {
    filtered = filtered.filter(s => s.language === selectedLanguage.value)
  }

  // 搜索过滤
  if (searchText.value) {
    const search = searchText.value.toLowerCase()
    filtered = filtered.filter(s => 
      s.original_text?.toLowerCase().includes(search) ||
      s.translated_text?.toLowerCase().includes(search)
    )
  }

  // 按时间排序
  const result = filtered.sort((a, b) => a.start_time - b.start_time)
  console.log('过滤后字幕数据数量:', result.length)
  return result
})

const sortedSubtitles = computed(() => {
  return [...props.subtitles].sort((a, b) => a.start_time - b.start_time)
})

const selectedSubtitleItems = computed(() => {
  return props.subtitles.filter(s => selectedSubtitles.value.includes(s.id))
})

const totalDuration = computed(() => {
  if (props.subtitles.length === 0) return 300 // 默认5分钟
  return Math.max(...props.subtitles.map(s => s.end_time)) + 10
})

const timeMarks = computed(() => {
  const marks = []
  const interval = Math.ceil(totalDuration.value / 20) // 大约20个标记
  for (let i = 0; i <= totalDuration.value; i += interval) {
    marks.push({ time: i })
  }
  return marks
})

// 工作流相关计算属性
const canProceedToNext = computed(() => {
  // 检查是否有字幕内容，并且至少有一条字幕有内容
  return props.subtitles.length > 0 && props.subtitles.some(s => s.original_text && s.original_text.trim().length > 0)
})

// 移除表格滚动高度计算，使用容器滚动

// 方法
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
  } else {
    return `${minutes}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
  }
}

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
}

const formatTimeInput = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '00:00:00,000'
  
  const hours = Math.floor(num / 3600)
  const minutes = Math.floor((num % 3600) / 60)
  const secs = Math.floor(num % 60)
  const ms = Math.floor((num % 1) * 1000)
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
}

const parseTimeInput = (value: string): number => {
  // 支持 HH:MM:SS,ms 格式
  const match = value.match(/^(\d{1,2}):(\d{2}):(\d{2}),(\d{3})$/)
  if (match) {
    const hours = parseInt(match[1])
    const minutes = parseInt(match[2])
    const seconds = parseInt(match[3])
    const milliseconds = parseInt(match[4])
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
  }
  
  // 回退到数字解析
  const num = parseFloat(value)
  return isNaN(num) ? 0 : num
}

const getLanguageName = (code: string): string => {
  const languageNames: Record<string, string> = {
    'zh': '中文',
    'en': '英语',
    'ja': '日语',
    'ko': '韩语',
    'fr': '法语',
    'de': '德语',
    'es': '西班牙语',
    'ru': '俄语'
  }
  return languageNames[code] || code
}

const onSubtitleClick = (subtitle: Subtitle) => {
  currentSubtitleId.value = subtitle.id
  emit('subtitle-click', subtitle)
  
  // 视频跳转到字幕开始时间（不自动播放）
  if (videoRef.value) {
    videoRef.value.currentTime = subtitle.start_time
  }
}

const onSubtitleItemClick = (event: MouseEvent, subtitle: Subtitle) => {
  // 如果点击的是复选框或其父元素，不触发字幕点击
  const target = event.target as HTMLElement
  if (target.closest('.ant-checkbox-wrapper') || target.closest('.subtitle-actions')) {
    return
  }
  onSubtitleClick(subtitle)
}

// 表格相关方法

const onIndexClick = (record: Subtitle) => {
  onSubtitleClick(record)
}

const getRowClassName = (record: Subtitle) => {
  const classes = []
  if (currentSubtitleId.value === record.id) {
    classes.push('active-row')
  }
  if (highlightedSubtitleId.value === record.id) {
    classes.push('highlighted-row')
  }
  if (selectedSubtitles.value.includes(record.id)) {
    classes.push('selected-row')
  }
  if (record.is_edited) {
    classes.push('edited-row')
  }
  return classes.join(' ')
}

const onTimeInputChange = (subtitle: Subtitle, type: 'start' | 'end', value: string) => {
  // 解析输入的时间字符串
  const newTime = parseTimeInput(value)
  
  if (isNaN(newTime) || newTime < 0) {
    message.error('请输入有效的时间格式 (HH:MM:SS,ms)')
    return
  }
  
  // 更新字幕时间
  if (type === 'start') {
    subtitle.start_time = newTime
  } else {
    subtitle.end_time = newTime
  }
  
  // 验证时间有效性
  if (type === 'start' && subtitle.start_time >= subtitle.end_time) {
    message.error('开始时间不能大于或等于结束时间')
    return
  }
  if (type === 'end' && subtitle.end_time <= subtitle.start_time) {
    message.error('结束时间不能小于或等于开始时间')
    return
  }
  
  // 自动计算duration
  subtitle.duration = subtitle.end_time - subtitle.start_time
  
  subtitle.is_edited = true
  emit('subtitle-update', subtitle)
}

const onTimeChange = (subtitle: Subtitle, type: 'start' | 'end') => {
  // 验证时间有效性
  if (type === 'start' && subtitle.start_time >= subtitle.end_time) {
    message.error('开始时间不能大于或等于结束时间')
    return
  }
  if (type === 'end' && subtitle.end_time <= subtitle.start_time) {
    message.error('结束时间不能小于或等于开始时间')
    return
  }
  
  // 自动计算duration
  subtitle.duration = subtitle.end_time - subtitle.start_time
  
  subtitle.is_edited = true
  emit('subtitle-update', subtitle)
}

const onTextChange = (subtitle: Subtitle, type: 'original') => {
  if (!subtitle.original_text || subtitle.original_text.trim() === '') {
    message.error('字幕内容不能为空')
    return
  }
  
  subtitle.is_edited = true
  emit('subtitle-update', subtitle)
}

// 文本框引用和光标位置相关方法
const setTextareaRef = (subtitleId: number, el: any) => {
  // 由于我们现在直接从事件获取textarea，这个方法可以简化
  if (el) {
    // 仅用于调试，实际不需要存储引用
    console.log(`📝 字幕 ${subtitleId} 的组件引用已设置`)
  }
}

const onTextareaFocus = (subtitleId: number) => {
  focusedSubtitleId.value = subtitleId
}

const onTextareaBlur = (subtitleId: number) => {
  if (focusedSubtitleId.value === subtitleId) {
    focusedSubtitleId.value = null
  }
}

const onTextareaClick = (subtitleId: number, event: Event) => {
  // 延迟一点时间确保光标位置已经更新
  setTimeout(() => {
    updateCursorPositionFromEvent(subtitleId, event)
  }, 10)
}

const onTextareaKeyup = (subtitleId: number, event: Event) => {
  updateCursorPositionFromEvent(subtitleId, event)
}

const onTextareaSelect = (subtitleId: number, event: Event) => {
  updateCursorPositionFromEvent(subtitleId, event)
}

const updateCursorPositionFromEvent = (subtitleId: number, event: Event) => {
  const target = event.target as HTMLTextAreaElement
  
  if (target && target.tagName === 'TEXTAREA') {
    const cursorPosition = target.selectionStart || 0
    cursorPositions.value.set(subtitleId, cursorPosition)
    console.log(`🎯 [光标拆分功能] 字幕 ${subtitleId} 光标位置: ${cursorPosition}`)
  }
}

// 拆分对话框中的光标位置处理方法
const onSplitTextareaClick = (textareaId: string, event: Event) => {
  setTimeout(() => {
    updateSplitCursorPositionFromEvent(textareaId, event)
  }, 10)
}

const onSplitTextareaKeyup = (textareaId: string, event: Event) => {
  updateSplitCursorPositionFromEvent(textareaId, event)
}

const onSplitTextareaSelect = (textareaId: string, event: Event) => {
  updateSplitCursorPositionFromEvent(textareaId, event)
}

const onSplitTextareaFocus = (textareaId: string) => {
  focusedSplitTextarea.value = textareaId
}

const onSplitTextareaBlur = (textareaId: string) => {
  // 延迟清除焦点，给按钮点击事件一些时间
  setTimeout(() => {
    if (focusedSplitTextarea.value === textareaId) {
      focusedSplitTextarea.value = null
    }
  }, 100)
}

const updateSplitCursorPositionFromEvent = (textareaId: string, event: Event) => {
  const target = event.target as HTMLTextAreaElement
  
  if (target && target.tagName === 'TEXTAREA') {
    const cursorPosition = target.selectionStart || 0
    splitDialogCursorPositions.value.set(textareaId, cursorPosition)
    console.log(`🎯 [拆分对话框光标] ${textareaId} 光标位置: ${cursorPosition}`)
  }
}

// 根据文本拆分比例计算时间分割点的通用函数
const calculateSplitTimeByTextRatio = (
  cursorPosition: number,
  totalTextLength: number,
  startTime: number,
  endTime: number
): number => {
  // 边界情况处理
  if (totalTextLength === 0 || cursorPosition <= 0 || cursorPosition >= totalTextLength) {
    return (startTime + endTime) / 2
  }
  
  // 确保时间范围有效
  if (startTime >= endTime) {
    return startTime + 0.1 // 至少间隔0.1秒
  }
  
  const textRatio = cursorPosition / totalTextLength
  const timeDuration = endTime - startTime
  const newTime = startTime + timeDuration * textRatio
  
  // 确保新时间在合理范围内，留出最小间隔
  const minInterval = 0.1 // 最小间隔0.1秒
  const adjustedNewTime = Math.max(
    startTime + minInterval,
    Math.min(newTime, endTime - minInterval)
  )
  
  console.log(`📊 智能时间计算: 光标${cursorPosition}/${totalTextLength} (${(textRatio * 100).toFixed(1)}%), 时间段${formatTime(startTime)}-${formatTime(endTime)} (${timeDuration.toFixed(2)}s), 结果${formatTime(adjustedNewTime)}`)
  
  return adjustedNewTime
}

// 这个方法已经不需要了，因为我们直接从事件获取光标位置

// 删除了冗余的提示信息方法，界面更简洁

// 获取文本框的友好显示名称
const getSplitTextareaDisplayName = (textareaId: string) => {
  if (textareaId === 'first_text') {
    return '字幕1'
  } else if (textareaId.startsWith('split_point_')) {
    const index = parseInt(textareaId.replace('split_point_', ''))
    return `拆分点${index + 1}`
  }
  return textareaId
}

// 视频播放相关方法
const onVideoLoaded = () => {
  // 视频加载完成，可以在这里添加初始化逻辑
  console.log('视频加载完成')
  if (videoRef.value) {
    console.log('视频时长:', videoRef.value.duration)
  }
}

const onTimeUpdate = () => {
  if (videoRef.value) {
    // 更新当前时间
    currentTime.value = videoRef.value.currentTime
    
    // 查找当前播放的字幕
    const currentSubtitle = props.subtitles.find(s => 
      currentTime.value >= s.start_time && currentTime.value <= s.end_time
    )
    
    if (currentSubtitle && currentSubtitleId.value !== currentSubtitle.id) {
      currentSubtitleId.value = currentSubtitle.id
      highlightedSubtitleId.value = currentSubtitle.id
    }
  }
}

const onVideoPlay = () => {
  isPlaying.value = true
}

const onVideoPause = () => {
  isPlaying.value = false
}

const onVideoSeeked = () => {
  // 视频跳转后的处理
  onTimeUpdate()
}

const togglePlay = () => {
  if (videoRef.value) {
    if (isPlaying.value) {
      videoRef.value.pause()
    } else {
      videoRef.value.play()
    }
  }
}

const seekBackward = () => {
  if (videoRef.value) {
    videoRef.value.currentTime = Math.max(0, videoRef.value.currentTime - 5)
  }
}

const seekForward = () => {
  if (videoRef.value) {
    videoRef.value.currentTime = Math.min(totalDuration.value, videoRef.value.currentTime + 5)
  }
}


// 工具栏按钮处理函数
const splitSelectedSubtitle = () => {
  if (selectedSubtitles.value.length === 1) {
    const subtitle = props.subtitles.find(s => s.id === selectedSubtitles.value[0])
    if (subtitle) {
      showSplitSubtitle(subtitle)
    }
  }
}

const mergeSelectedSubtitles = () => {
  if (selectedSubtitles.value.length >= 2) {
    emit('subtitles-merge', selectedSubtitles.value)
    // 清除选中状态
    selectedSubtitles.value = []
  }
}

const deleteSelectedSubtitles = () => {
  if (selectedSubtitles.value.length > 0) {
    // 批量删除选中的字幕
    selectedSubtitles.value.forEach(subtitleId => {
      emit('subtitle-delete', subtitleId)
    })
    selectedSubtitles.value = []
  }
}

const onSubtitleAction = (action: string, subtitle: Subtitle) => {
  contextMenu.value.visible = false
  
  switch (action) {
    case 'split':
      showSplitSubtitle(subtitle)
      break
    case 'merge':
      if (selectedSubtitles.value.length < 2) {
        message.error('请至少选择两个字幕进行合并')
        return
      }
      emit('subtitles-merge', selectedSubtitles.value)
      // 清除选中状态
      selectedSubtitles.value = []
      break
    case 'duplicate':
      duplicateSubtitle(subtitle)
      break
    case 'delete':
      emit('subtitle-delete', subtitle.id)
      break
  }
  
  // 对于某些操作，也跳转到字幕时间
  if (['split', 'duplicate'].includes(action)) {
    if (videoRef.value) {
      videoRef.value.currentTime = subtitle.start_time
    }
  }
}

const isSubtitleSelected = (subtitleId: number): boolean => {
  return selectedSubtitles.value.includes(subtitleId)
}

const toggleSubtitleSelection = (subtitleId: number) => {
  const index = selectedSubtitles.value.indexOf(subtitleId)
  if (index > -1) {
    // 取消选中
    selectedSubtitles.value.splice(index, 1)
  } else {
    // 选中
    selectedSubtitles.value.push(subtitleId)
  }
}

const onSubtitleSelect = (checked: boolean, subtitleId: number) => {
  if (checked) {
    if (!selectedSubtitles.value.includes(subtitleId)) {
      selectedSubtitles.value.push(subtitleId)
    }
  } else {
    const index = selectedSubtitles.value.indexOf(subtitleId)
    if (index > -1) {
      selectedSubtitles.value.splice(index, 1)
    }
  }
}

const onSearch = () => {
  // 搜索逻辑已通过计算属性处理
}

const addSubtitle = () => {
  const newSubtitle: Partial<Subtitle> = {
    project_id: props.projectId,
    start_time: props.currentTime,
    end_time: props.currentTime + 5, // 默认5秒长度
    original_text: '',
    translated_text: '',
    language: selectedLanguage.value || 'zh',
    order_index: props.subtitles.length
  }
  
  emit('subtitle-create', newSubtitle)
}

const duplicateSubtitle = (subtitle: Subtitle) => {
  const duration = subtitle.duration || (subtitle.end_time - subtitle.start_time)
  const newSubtitle: Partial<Subtitle> = {
    project_id: props.projectId,
    start_time: subtitle.end_time,
    end_time: subtitle.end_time + duration,
    duration: duration,
    original_text: subtitle.original_text,
    translated_text: subtitle.translated_text,
    translated_text_multiline: subtitle.translated_text_multiline,
    language: subtitle.language,
    order_index: subtitle.order_index + 1
  }
  
  emit('subtitle-create', newSubtitle)
  message.success('字幕复制成功')
}

const saveProject = async () => {
  saving.value = true
  try {
    emit('save-project')
    // 成功/失败提示由父组件 handleSave 统一展示，此处不再重复提示
    } catch (error) {
      console.error('保存项目失败:', error)
    } finally {
    saving.value = false
  }
}

// 工作流：字幕编辑完成后进入翻译校验（机翻前不强制指派）
const goToTranslationReview = async () => {
  if (!canProceedToNext.value) {
    message.warning('请至少添加一条字幕内容后再继续')
    return
  }
  await saveProject()
  workflowStep.value = VIDEO_PIPELINE_STEP.AI_TRANSLATION
  emit('workflow-next-step')
}

const showSplitSubtitle = (subtitle: Subtitle) => {
  const originalText = subtitle.original_text || ''
  let cursorPosition = cursorPositions.value.get(subtitle.id)
  
  // 如果没有保存的光标位置，尝试从当前活动元素获取
  if (cursorPosition === undefined || cursorPosition === 0) {
    const activeElement = document.activeElement as HTMLTextAreaElement
    if (activeElement && activeElement.tagName === 'TEXTAREA') {
      const textareaValue = activeElement.value || ''
      // 检查这个textarea是否属于当前字幕
      if (textareaValue === originalText) {
        cursorPosition = activeElement.selectionStart || 0
        cursorPositions.value.set(subtitle.id, cursorPosition)
        console.log(`🔄 从活动元素获取光标位置: ${cursorPosition}`)
      }
    }
  }
  
  // 检查是否有光标位置且光标在文本中间
  const hasCursorPosition = cursorPosition !== undefined && 
                           cursorPosition > 0 && 
                           cursorPosition < originalText.length
  
  let firstText = ''
  let secondText = ''
  
  let splitTime = (subtitle.start_time + subtitle.end_time) / 2 // 默认中间值
  
  if (hasCursorPosition && cursorPosition !== undefined) {
    // 基于光标位置自动拆分文本
    firstText = originalText.substring(0, cursorPosition)
    secondText = originalText.substring(cursorPosition)
    
    // 根据文本拆分比例计算时间分割点
    splitTime = calculateSplitTimeByTextRatio(
      cursorPosition,
      originalText.length,
      subtitle.start_time,
      subtitle.end_time
    )
    
    console.log(`基于光标位置拆分: 位置=${cursorPosition}/${originalText.length}, 第一部分="${firstText}", 第二部分="${secondText}", 拆分时间=${formatTime(splitTime)}`)
    
    message.success(`已根据光标位置自动拆分文本`)
  } else {
    // 没有光标位置，使用原有逻辑
    firstText = originalText
    secondText = ''
    
    if (cursorPosition !== undefined) {
      console.log(`光标位置无效: ${cursorPosition}, 文本长度: ${originalText.length}`)
    } else {
      console.log('没有检测到光标位置，使用手动拆分模式')
    }
  }
  
  splitData.value = {
    subtitle_id: subtitle.id,
    min_time: subtitle.start_time,
    max_time: subtitle.end_time,
    first_text: firstText,
    split_points: [
      {
        time: splitTime,
        text: secondText
      }
    ]
  }
  showSplitDialog.value = true
}

// 智能添加拆分点（基于光标位置）
const addSplitPointSmart = () => {
  // 智能选择要操作的文本框
  const availableTextareas = Array.from(splitDialogCursorPositions.value.keys())
  
  if (availableTextareas.length === 0) {
    message.warning('请先在文本框中点击要拆分的位置，然后点击智能添加拆分点')
    return
  }
  
  // 选择策略：优先选择有效光标位置的文本框
  let bestTextarea = null
  let bestScore = -1
  
  for (const textareaId of availableTextareas) {
    const cursorPos = splitDialogCursorPositions.value.get(textareaId)
    if (cursorPos === undefined) continue
    
    // 计算这个文本框的适合度分数
    let score = 0
    
    if (textareaId === 'first_text') {
      const text = splitData.value.first_text || ''
      if (cursorPos > 0 && cursorPos < text.length) {
        score = 100 + cursorPos // 字幕1优先级高
      }
    } else if (textareaId.startsWith('split_point_')) {
      const index = parseInt(textareaId.replace('split_point_', ''))
      const point = splitData.value.split_points[index]
      if (point && point.text) {
        if (cursorPos > 0 && cursorPos < point.text.length) {
          score = 50 + cursorPos // 拆分点优先级中等
        }
      }
    }
    
    if (score > bestScore) {
      bestScore = score
      bestTextarea = textareaId
    }
  }
  
  if (!bestTextarea) {
    message.warning('未找到有效的光标位置，请确保光标在文本中间位置')
    return
  }
  
  const focusedTextarea = bestTextarea
  
  const cursorPosition = splitDialogCursorPositions.value.get(focusedTextarea)
  if (cursorPosition === undefined) {
    message.warning('未检测到光标位置，请在文本框中点击要拆分的位置')
    return
  }
  
  if (focusedTextarea === 'first_text') {
    // 在第一个文本框中拆分
    const originalText = splitData.value.first_text
    if (cursorPosition <= 0 || cursorPosition >= originalText.length) {
      message.warning('光标位置无效，请将光标放在文本中间位置')
      return
    }
    
    // 拆分文本
    const firstPart = originalText.substring(0, cursorPosition)
    const secondPart = originalText.substring(cursorPosition)
    
    // 更新第一个文本框的内容
    splitData.value.first_text = firstPart
    
    // 字幕1拆分：应该在整个字幕的时间段内按比例分割
    const startTime = splitData.value.min_time
    const endTime = splitData.value.max_time
    
    // 根据文本拆分比例计算时间分割点
    const newTime = calculateSplitTimeByTextRatio(
      cursorPosition,
      originalText.length,
      startTime,
      endTime
    )
    
    // 在开头插入新的拆分点
    // 使用计算出的新时间作为拆分点时间
    splitData.value.split_points.unshift({
      time: newTime,
      text: secondPart
    })
    
    message.success(`已在光标位置（第${cursorPosition}个字符）智能添加拆分点`)
    
  } else if (focusedTextarea.startsWith('split_point_')) {
    // 在某个拆分点的文本框中拆分
    const pointIndex = parseInt(focusedTextarea.replace('split_point_', ''))
    const point = splitData.value.split_points[pointIndex]
    
    if (!point || !point.text) {
      message.warning('该拆分点没有文本内容，无法进行拆分')
      return
    }
    
    if (cursorPosition <= 0 || cursorPosition >= point.text.length) {
      message.warning('光标位置无效，请将光标放在文本中间位置')
      return
    }
    
    // 拆分文本
    const originalPointText = point.text
    const firstPart = originalPointText.substring(0, cursorPosition)
    const secondPart = originalPointText.substring(cursorPosition)
    
    // 光标前的内容保持位置不变，光标后的内容向下移动
    // 当前拆分点保持光标前的内容
    point.text = firstPart
    
    // 确定当前拆分点对应的时间段
    const segmentStartTime = pointIndex > 0 
      ? splitData.value.split_points[pointIndex - 1].time 
      : splitData.value.min_time
    // 拆分点的时间段应该延伸到下一个拆分点或整个字幕的结束时间
    const segmentEndTime = pointIndex < splitData.value.split_points.length - 1
      ? splitData.value.split_points[pointIndex + 1].time
      : splitData.value.max_time
    
    // 根据文本拆分比例计算时间分割点
    const newTime = calculateSplitTimeByTextRatio(
      cursorPosition,
      originalPointText.length,
      segmentStartTime,
      segmentEndTime
    )
    
    // 在当前拆分点后插入新的拆分点，光标后的内容向下移动到这里
    splitData.value.split_points.splice(pointIndex + 1, 0, {
      time: newTime,
      text: secondPart
    })
    
    message.success(`已在拆分点${pointIndex + 1}的光标位置（第${cursorPosition}个字符）智能添加新拆分点`)
  }
  
  // 清除光标位置记录
  splitDialogCursorPositions.value.delete(focusedTextarea)
}

// 手动添加拆分点（原有逻辑）
const addSplitPoint = () => {
  console.log('🔧 手动添加拆分点被调用')
  
  const lastPoint = splitData.value.split_points[splitData.value.split_points.length - 1]
  const newTime = lastPoint ? 
    Math.min(lastPoint.time + 2, splitData.value.max_time - 0.1) : 
    (splitData.value.min_time + splitData.value.max_time) / 2
  
  console.log('添加新拆分点，时间:', newTime)
  
  splitData.value.split_points.push({
    time: newTime,
    text: ''
  })
  
  console.log('拆分点已添加，当前拆分点数量:', splitData.value.split_points.length)
}

// 删除拆分点
const removeSplitPoint = (index: number) => {
  if (splitData.value.split_points.length > 1) {
    splitData.value.split_points.splice(index, 1)
  }
}

// 更新拆分点时间
const updateSplitPointTime = (index: number, newTime: number) => {
  if (newTime >= splitData.value.min_time && newTime <= splitData.value.max_time) {
    splitData.value.split_points[index].time = newTime
  }
}

// 获取按时间排序的拆分点（用于显示）
const getSortedSplitPoints = () => {
  return splitData.value.split_points
    .map((point, originalIndex) => ({ ...point, originalIndex }))
    .sort((a, b) => a.time - b.time)
}

// 获取拆分预览
const getSplitPreview = () => {
  if (splitData.value.split_points.length === 0) return []
  
  // 按时间排序拆分点，但保持原始索引以获取正确的文本内容
  const sortedPoints = [...splitData.value.split_points]
    .map((point, originalIndex) => ({ ...point, originalIndex }))
    .sort((a, b) => a.time - b.time)
  
  const preview = []
  
  // 第一个字幕：从开始到第一个拆分点，使用用户输入的字幕1文本
  preview.push({
    start_time: splitData.value.min_time,
    end_time: sortedPoints[0].time,
    text: splitData.value.first_text || '（空内容）'
  })
  
  // 后续字幕：按时间顺序生成字幕段，使用排序后的拆分点文本内容
  for (let i = 0; i < sortedPoints.length; i++) {
    const startTime = sortedPoints[i].time
    const endTime = i < sortedPoints.length - 1 ? sortedPoints[i + 1].time : splitData.value.max_time
    
    // 使用排序后的拆分点文本内容
    preview.push({
      start_time: startTime,
      end_time: endTime,
      text: sortedPoints[i].text || '（空内容）'
    })
  }
  
  return preview
}

const executeSplit = () => {
  // 验证拆分点
  if (splitData.value.split_points.length === 0) {
    message.error('请至少添加一个拆分点')
    return
  }
  
  // 验证时间顺序
  const sortedPoints = [...splitData.value.split_points].sort((a, b) => a.time - b.time)
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    if (sortedPoints[i].time >= sortedPoints[i + 1].time) {
      message.error('拆分点时间必须按顺序排列')
      return
    }
  }
  
  // 验证文本内容
  if (!splitData.value.first_text.trim()) {
    message.error('字幕1的文本内容不能为空')
    return
  }
  
  for (let i = 0; i < sortedPoints.length; i++) {
    if (!sortedPoints[i].text.trim()) {
      message.error(`拆分点 ${i + 1} 的文本内容不能为空`)
      return
    }
  }
  
  // 根据拆分点数量决定使用哪种拆分方式
  if (sortedPoints.length === 1) {
    // 单条拆分：发送 split_time, first_text, second_text
    emit('subtitle-split', splitData.value.subtitle_id, {
      split_time: sortedPoints[0].time,
      first_text: splitData.value.first_text,
      second_text: sortedPoints[0].text
    })
  } else {
    // 多条拆分：发送 first_text 和 split_points
    emit('subtitle-split', splitData.value.subtitle_id, {
      first_text: splitData.value.first_text,
      split_points: sortedPoints
    })
  }
  
  showSplitDialog.value = false
  
  // 清除选中状态
  selectedSubtitles.value = []
}

const resetSplitDialog = () => {
  showSplitDialog.value = false
  splitData.value = {
    subtitle_id: 0,
    min_time: 0,
    max_time: 0,
    first_text: '',
    split_points: []
  }
}

const executeBatchOperation = () => {
  emit('batch-operation', { ...batchOperation.value })
  showBatchOperations.value = false
}

const resetBatchOperation = () => {
  showBatchOperations.value = false
  batchOperation.value = {
    operation: 'replace',
    search_text: '',
    replace_text: '',
    start_time: 0,
    end_time: 0,
    delete_text: ''
  }
}

// 时间轴相关方法
const startResize = (event: MouseEvent, subtitle: Subtitle, type: 'start' | 'end') => {
  event.stopPropagation()
  isResizing.value = true
  resizeData.value = {
    subtitle,
    type,
    startX: event.clientX,
    startTime: type === 'start' ? subtitle.start_time : subtitle.end_time
  }
  
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}

const onResize = (event: MouseEvent) => {
  if (!isResizing.value || !resizeData.value.subtitle) return
  
  const deltaX = event.clientX - resizeData.value.startX
  const timelineWidth = timelineRef.value?.clientWidth || 1000
  const deltaTime = (deltaX / timelineWidth) * totalDuration.value
  
  const newTime = resizeData.value.startTime + deltaTime
  const subtitle = resizeData.value.subtitle
  
  if (resizeData.value.type === 'start') {
    subtitle.start_time = Math.max(0, Math.min(newTime, subtitle.end_time - 0.1))
  } else {
    subtitle.end_time = Math.max(subtitle.start_time + 0.1, newTime)
  }
  
  // 自动计算duration
  subtitle.duration = subtitle.end_time - subtitle.start_time
  
  subtitle.is_edited = true
  
  // 防抖更新，避免频繁触发更新事件
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  updateTimer = setTimeout(() => {
    emit('subtitle-update', subtitle)
  }, 100)
}

const stopResize = () => {
  if (isResizing.value && resizeData.value.subtitle) {
    // 确保最终更新时duration正确
    const subtitle = resizeData.value.subtitle
    subtitle.duration = subtitle.end_time - subtitle.start_time
    
    // 拖拽结束时发送最终更新
    if (updateTimer) {
      clearTimeout(updateTimer)
    }
    emit('subtitle-update', subtitle)
  }
  
  isResizing.value = false
  resizeData.value = {
    subtitle: null,
    type: 'start',
    startX: 0,
    startTime: 0
  }
  
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

const showSubtitleContextMenu = (event: MouseEvent, subtitle: Subtitle) => {
  contextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    subtitle
  }
}

const hideContextMenu = () => {
  contextMenu.value.visible = false
}

const scrollToCurrentSubtitle = () => {
  if (!currentSubtitleId.value || !subtitleListRef.value) return
  
  const subtitleElement = subtitleListRef.value.querySelector(
    `.subtitle-item[data-id="${currentSubtitleId.value}"]`
  ) as HTMLElement
  
  if (subtitleElement) {
    subtitleElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }
}

// 监听当前时间变化，高亮对应字幕
watch(() => props.currentTime, (newTime) => {
  const currentSubtitle = props.subtitles.find(s => 
    newTime >= s.start_time && newTime <= s.end_time
  )
  
  if (currentSubtitle) {
    highlightedSubtitleId.value = currentSubtitle.id
    
    // 自动滚动到当前字幕
    if (!showTimelineView.value) {
      nextTick(() => {
        const element = subtitleListRef.value?.querySelector(
          `.subtitle-item.highlighted`
        ) as HTMLElement
        
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      })
    }
  } else {
    highlightedSubtitleId.value = null
  }
})

// 加载字符数统计
const loadCharacterCount = async () => {
  try {
    // 临时模拟数据
    characterCount.value = {
      project_id: props.projectId,
      language: selectedLanguage.value || undefined,
      original_character_count: props.subtitles.reduce((sum, s) => sum + (s.original_text?.length || 0), 0),
      subtitle_count: props.subtitles.length,
      total_duration: props.subtitles.reduce((sum, s) => sum + (s.duration || (s.end_time - s.start_time)), 0),
      last_updated: new Date().toISOString()
    }
  } catch (error) {
    console.error('加载字符数统计失败:', error)
  }
}

// 窗口大小变化监听
const handleResize = () => {
  // 触发响应式更新
  nextTick(() => {
    // 强制重新计算表格高度
  })
}

// 生命周期
onMounted(() => {
  loadCharacterCount()
  document.addEventListener('click', hideContextMenu)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  document.removeEventListener('click', hideContextMenu)
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  window.removeEventListener('resize', handleResize)
})

// 监听字幕变化，重新计算统计 - 使用浅层监听优化性能
watch(() => props.subtitles.length, () => {
  loadCharacterCount()
}, { deep: true })

// 监听语言变化，重新计算统计
watch(selectedLanguage, () => {
  loadCharacterCount()
})

// 暴露方法给父组件
const highlightSubtitle = (subtitleId: number) => {
  highlightedSubtitleId.value = subtitleId
}

const scrollToSubtitle = (subtitleId: number) => {
  if (!showTimelineView.value && subtitleListRef.value) {
    nextTick(() => {
      const element = subtitleListRef.value?.querySelector(
        `.subtitle-item[data-id="${subtitleId}"]`
      ) as HTMLElement
      
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    })
  }
}

defineExpose({
  highlightSubtitle,
  scrollToSubtitle
})
</script>

<style scoped>
.enhanced-subtitle-editor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 24px;
  overflow: hidden; /* 防止整体溢出 */
}

/* 工作流步骤样式 */
.workflow-steps-container {
  margin-bottom: 24px;
  flex-shrink: 0; /* 防止被压缩 */
}

.workflow-steps {
  background: white;
  padding: 16px 24px; /* 减少上下内边距 */
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.workflow-steps :deep(.ant-steps-item-title) {
  font-size: 16px;
  font-weight: 500;
}

.workflow-steps :deep(.ant-steps-item-description) {
  font-size: 12px;
  color: #666;
}

.subtitle-content-area {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 0; /* 使用 0 而不是 auto，配合 flex: 1 确保正确计算 */
}

.subtitle-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.character-count {
  padding: 16px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin-bottom: 16px;
}

/* 视频播放组件样式 */
.video-player-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  overflow: hidden;
  width: 50%;
  margin-left: auto;
  margin-right: auto;
  flex-shrink: 0; /* 防止视频播放器被压缩 */
}

.video-player {
  position: relative;
  width: 100%;
  height: 300px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.video-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.video-time-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: monospace;
  font-size: 14px;
  color: #666;
}

.video-time-info .separator {
  color: #999;
}

.current-time {
  color: #1890ff;
  font-weight: 500;
}

.total-time {
  color: #666;
}

/* 时间轴视图样式 */
.timeline-view {
  flex: 1;
  overflow: hidden;
  background: white;
  margin: 16px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 确保 flex 子元素可以正确收缩 */
}

.timeline-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.timeline-header {
  height: 40px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.time-ruler {
  position: relative;
  height: 100%;
}

.time-mark {
  position: absolute;
  top: 0;
  height: 100%;
  border-left: 1px solid #d9d9d9;
}

.time-label {
  position: absolute;
  top: 5px;
  left: 5px;
  font-size: 12px;
  color: #666;
}

.timeline-tracks {
  flex: 1;
  position: relative;
  overflow-y: auto;
  padding: 20px 0;
}

.playhead {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #ff4d4f;
  z-index: 10;
  pointer-events: none;
}

.subtitle-track {
  position: relative;
  height: 60px;
  margin: 0 10px;
}

.subtitle-block {
  position: absolute;
  height: 50px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  min-width: 20px;
}

.subtitle-block:hover {
  background: #bae7ff;
  border-color: #40a9ff;
  z-index: 5;
}

.subtitle-block.active {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
  z-index: 6;
}

.subtitle-block.highlighted {
  background: #52c41a;
  border-color: #52c41a;
  color: white;
  z-index: 7;
}

.subtitle-block.selected {
  background: #faad14;
  border-color: #faad14;
  color: white;
}

.subtitle-block.edited {
  border-left: 4px solid #ff7a45;
}

.subtitle-content {
  flex: 1;
  overflow: hidden;
}

.subtitle-text {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subtitle-time {
  font-size: 10px;
  opacity: 0.8;
  white-space: nowrap;
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background: rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.subtitle-block:hover .resize-handle {
  opacity: 1;
}

.resize-start {
  left: -3px;
}

.resize-end {
  right: -3px;
}

/* 表格视图样式 */
.subtitle-table-container {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  /* 移除固定高度，使用 flex: 1 自适应 */
}

.subtitle-table-container .ant-table {
  border-radius: 8px;
}

.subtitle-table-container .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  border-bottom: 2px solid #f0f0f0;
}

.subtitle-table-container .ant-table-tbody > tr {
  cursor: pointer;
  transition: all 0.2s ease;
}

.subtitle-table-container .ant-table-tbody > tr:hover {
  background: #f5f5f5;
}

.subtitle-table-container .ant-table-tbody > tr.active-row {
  background: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.subtitle-table-container .ant-table-tbody > tr.highlighted-row {
  background: #fff7e6;
}

.subtitle-table-container .ant-table-tbody > tr.selected-row {
  background: #f0f9ff;
}

.subtitle-table-container .ant-table-tbody > tr.edited-row {
  background: #fffbe6;
}

.subtitle-index {
  font-weight: 600;
  color: #1890ff;
}

.clickable-index {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clickable-index:hover {
  background: #e6f7ff;
  color: #1890ff;
}

.duration-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.duration-warning {
  color: #ff4d4f;
  font-weight: 600;
}

.subtitle-status {
  display: flex;
  justify-content: center;
}

/* 列表视图样式 */
.subtitle-list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subtitle-item {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin: 12px 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.subtitle-item:hover {
  border-color: #d9d9d9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subtitle-item.active {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.subtitle-item.highlighted {
  background: #e6f7ff;
  border-color: #91d5ff;
}

.subtitle-item.selected {
  background: #fff7e6;
  border-color: #ffd591;
}

.subtitle-item.edited {
  border-left: 4px solid #faad14;
}

.subtitle-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
}

.subtitle-index {
  background: #f0f0f0;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  min-width: 30px;
  text-align: center;
}

.subtitle-time {
  flex: 1;
}

.subtitle-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.subtitle-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subtitle-text-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.subtitle-text-group label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.subtitle-text-group .ant-input.error,
.subtitle-text-group .ant-input.error:focus {
  border-color: #ff4d4f;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
}

.subtitle-status-inline {
  display: flex;
  align-items: center;
  margin-left: 8px;
  margin-right: 8px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

/* 右键菜单样式 */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 120px;
}

.context-menu-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

.context-menu-item.danger {
  color: #ff4d4f;
}

.context-menu-item.danger:hover {
  background: #fff2f0;
}

.context-menu-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
}

/* 批量操作样式 */
.no-selection {
  padding: 16px;
  text-align: center;
}

/* 拆分对话框样式 */
.split-dialog-content {
  /* 移除固定高度和滚动，让Modal自己处理 */
  display: flex;
  flex-direction: column;
}

.split-points-container {
  margin-bottom: 24px;
}

.split-points-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-weight: 600;
  color: #262626;
}

.empty-split-points {
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
}

.split-points-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 字幕1区域样式 */
.subtitle-1-section {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.subtitle-1-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.subtitle-1-label {
  font-weight: 600;
  color: #52c41a;
  font-size: 14px;
}

.subtitle-1-time {
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.split-point-item {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.split-point-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.split-point-label {
  font-weight: 600;
  color: #1890ff;
}

.split-point-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.split-preview {
  margin-top: 16px;
}

.preview-subtitles {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* 移除固定高度和滚动，让外层Modal处理 */
}

.preview-subtitle {
  border: 1px solid #e6f7ff;
  border-radius: 4px;
  padding: 8px 12px;
  background: #f6ffed;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.preview-label {
  font-weight: 600;
  color: #52c41a;
}

.preview-time {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #666;
}

.preview-text {
  color: #262626;
  line-height: 1.5;
}

/* 响应式设计 - 小屏幕（15寸及以下） */
@media (max-height: 900px) {
  .enhanced-subtitle-editor {
    padding: 12px; /* 减少内边距 */
  }
  
  .workflow-steps-container {
    margin-bottom: 12px; /* 减少间距 */
  }
  
  .workflow-steps {
    padding: 12px 16px; /* 进一步减少内边距 */
  }
  
  .workflow-steps :deep(.ant-steps-item-title) {
    font-size: 14px;
  }
  
  .workflow-steps :deep(.ant-steps-item-description) {
    font-size: 11px;
  }
  
  .character-count {
    padding: 12px 16px;
    margin-bottom: 12px;
  }
  
  .video-player-container {
    margin-bottom: 12px;
  }
  
  .video-player {
    height: 240px; /* 减小视频播放器高度 */
  }
  
  .subtitle-toolbar {
    padding: 12px 16px;
    margin-bottom: 12px;
  }
  
  .subtitle-content-area {
    /* 确保字幕编辑区域可见 */
    min-height: 200px; /* 设置最小高度确保可见 */
  }
  
  .subtitle-table-container {
    /* 在小屏幕上确保表格容器可见 */
    min-height: 200px;
  }
}

/* 响应式设计 - 移动端 */
@media (max-width: 768px) {
  .subtitle-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .toolbar-left,
  .toolbar-right {
    justify-content: center;
  }
  
  .character-count .ant-row {
    text-align: center;
  }
  
  .subtitle-header {
    flex-wrap: wrap;
  }
  
  .subtitle-time {
    order: 3;
    flex-basis: 100%;
    margin-top: 8px;
  }
  
  .timeline-view {
    margin: 8px;
  }
  
  .subtitle-block {
    height: 40px;
    font-size: 11px;
  }
}
</style>